<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import LoadingState from '../../../components/widgets/LoadingState.vue';
import RealEstateTabs from './RealEstateTabs.vue';
import AgentModal from './modals/AgentModal.vue';
import AgentLeaveModal from './modals/AgentLeaveModal.vue';
import PortalAccessModal from './modals/PortalAccessModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../api/realEstate/errors';
import { findPortalUser, portalAccessStatus } from './helpers';

// The store holds every agent (all API pages, since the dropdowns need
// them all), so this page only slices it for display.
const PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default {
  name: 'AgentsList',
  components: {
    PageHeader,
    PaginationBar,
    DeleteModal,
    LoadingState,
    RealEstateTabs,
    AgentModal,
    AgentLeaveModal,
    PortalAccessModal,
  },
  data() {
    return {
      isLoading: false,
      isModalOpen: false,
      editingAgent: null,
      agentToDelete: null,
      isDeleting: false,
      blockedAgent: null,
      leaveAgent: null,
      accessAgent: null,
      page: 1,
      pageSize: PAGE_SIZE,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    };
  },
  computed: {
    ...mapGetters('realEstateAgents', { agents: 'getAgents' }),
    // The account's portal users (Settings → Agents), matched by email.
    ...mapGetters('agents', { portalUsers: 'getAgents' }),
    totalPages() {
      return Math.max(1, Math.ceil(this.agents.length / this.pageSize));
    },
    pagedAgents() {
      const start = (this.page - 1) * this.pageSize;
      return this.agents.slice(start, start + this.pageSize);
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
  },
  watch: {
    // Deleting the last agent on the last page would leave it empty.
    totalPages(pages) {
      if (this.page > pages) this.page = pages;
    },
  },
  mounted() {
    this.loadAgents();
  },
  methods: {
    ...mapActions('realEstateAgents', ['fetchAgents', 'removeAgent']),
    async loadAgents() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchAgents(),
          this.$store.dispatch('agents/get'),
        ]);
      } finally {
        this.isLoading = false;
      }
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
    },
    openCreateModal() {
      this.editingAgent = null;
      this.isModalOpen = true;
    },
    openEditModal(agent) {
      this.editingAgent = agent;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.editingAgent = null;
    },
    accessStatus(agent) {
      return portalAccessStatus(agent, this.portalUsers);
    },
    accessTitle(agent) {
      const user = findPortalUser(agent, this.portalUsers);
      if (!user) return '';
      return this.$t('REAL_ESTATE.AGENTS.ACCESS_ROLE_TITLE', {
        role: this.$t(
          `AGENT_MGMT.AGENT_TYPES.${String(user.role).toUpperCase()}`
        ),
      });
    },
    openLeaveModal(agent) {
      this.leaveAgent = agent;
    },
    handleDelete(agent) {
      this.blockedAgent = null;
      this.agentToDelete = agent;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeAgent(this.agentToDelete.agent_id);
        useAlert(this.$t('REAL_ESTATE.AGENTS.SUCCESS_DELETED'));
        this.agentToDelete = null;
      } catch (err) {
        if (apiErrorCode(err) === 'has_appointments') {
          this.blockedAgent = this.agentToDelete;
        } else {
          useAlert(
            apiErrorMessage(this, err, 'REAL_ESTATE.AGENTS.ERROR_DELETE')
          );
        }
        this.agentToDelete = null;
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <div class="p-4 md:p-8 h-full flex flex-col overflow-hidden flex-1 min-w-0">
    <AgentModal v-if="isModalOpen" :agent="editingAgent" @close="closeModal" />
    <PortalAccessModal
      v-if="accessAgent"
      :agent="accessAgent"
      @close="accessAgent = null"
    />
    <AgentLeaveModal
      v-if="leaveAgent"
      :agent="leaveAgent"
      @close="leaveAgent = null"
    />
    <DeleteModal
      v-if="agentToDelete"
      :delete-name="agentToDelete.name"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
      @close="agentToDelete = null"
    />

    <PageHeader
      :title="$t('REAL_ESTATE.LIST.HEADER_TITLE')"
      icon="condo"
      :description="$t('REAL_ESTATE.AGENTS.HEADER_DESCRIPTION')"
    >
      <template #actions>
        <woot-button icon="add" @click="openCreateModal">
          {{ $t('REAL_ESTATE.AGENTS.NEW_BUTTON') }}
        </woot-button>
      </template>
    </PageHeader>

    <RealEstateTabs class="mb-6" />

    <p
      v-if="blockedAgent"
      class="mb-4 flex items-center gap-2 rounded-md bg-amber-50 text-amber-700 text-sm px-3 py-2"
    >
      <fluent-icon icon="warning" size="14" />
      <span>
        {{
          $t('REAL_ESTATE.AGENTS.HAS_APPOINTMENTS_WARNING', {
            name: blockedAgent.name,
          })
        }}
      </span>
      <router-link
        :to="{ name: 'real_estate_appointments' }"
        class="text-woot-500 hover:underline font-medium shrink-0"
      >
        {{ $t('REAL_ESTATE.AGENTS.HAS_APPOINTMENTS_LINK') }}
      </router-link>
    </p>

    <div class="flex-1 overflow-y-auto">
      <LoadingState
        v-if="isLoading"
        :message="$t('REAL_ESTATE.AGENTS.LOADING')"
      />
      <div
        v-else-if="agents.length === 0"
        class="flex flex-col items-center justify-center py-12"
      >
        <fluent-icon
          icon="person"
          class="text-slate-300 dark:text-slate-600 mb-4"
          size="48"
        />
        <p class="text-slate-500 dark:text-slate-400 text-center">
          {{ $t('REAL_ESTATE.AGENTS.EMPTY_STATE') }}
        </p>
      </div>
      <table v-else class="w-full text-sm border-collapse">
        <thead>
          <tr
            class="text-left text-slate-400 border-b border-slate-200 dark:border-slate-700"
          >
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.AGENTS.COLUMN_NAME') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.AGENTS.COLUMN_EMAIL') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.AGENTS.COLUMN_PHONE') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.AGENTS.COLUMN_PROJECTS') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.AGENTS.COLUMN_ACCESS') }}
            </th>
            <th class="py-2 pr-4 font-medium text-right">
              {{ $t('REAL_ESTATE.AGENTS.COLUMN_ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in pagedAgents"
            :key="agent.agent_id"
            class="border-b border-slate-100 dark:border-slate-800"
          >
            <td
              class="py-2 pr-4 text-slate-900 dark:text-slate-100 font-medium"
            >
              {{ agent.name }}
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{ agent.email || '—' }}
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{ agent.phone || '—' }}
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{ agent.property_ids.length }}
            </td>
            <td class="py-2 pr-4">
              <span
                v-if="accessStatus(agent) === 'active'"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                :title="accessTitle(agent)"
              >
                {{ $t('REAL_ESTATE.AGENTS.ACCESS_ACTIVE') }}
              </span>
              <span
                v-else-if="accessStatus(agent) === 'pending'"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300"
                :title="accessTitle(agent)"
              >
                {{ $t('REAL_ESTATE.AGENTS.ACCESS_PENDING') }}
              </span>
              <span v-else class="inline-flex items-center gap-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
                >
                  {{ $t('REAL_ESTATE.AGENTS.ACCESS_NONE') }}
                </span>
                <woot-button
                  variant="link"
                  size="small"
                  @click="accessAgent = agent"
                >
                  {{ $t('REAL_ESTATE.AGENTS.ACCESS_GIVE') }}
                </woot-button>
              </span>
            </td>
            <td class="py-2 pr-4 text-right">
              <woot-button
                variant="clear"
                size="small"
                icon="calendar-clock"
                :title="$t('REAL_ESTATE.AGENT_LEAVE.LEAVE_BUTTON')"
                @click="openLeaveModal(agent)"
              >
                {{ $t('REAL_ESTATE.AGENT_LEAVE.LEAVE_BUTTON') }}
              </woot-button>
              <woot-button
                variant="clear"
                size="small"
                icon="edit"
                @click="openEditModal(agent)"
              />
              <woot-button
                variant="clear"
                size="small"
                color-scheme="alert"
                icon="delete"
                @click="handleDelete(agent)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <PaginationBar
        v-if="!isLoading && agents.length > pageSizeOptions[0]"
        :pagination="pagination"
        :page-size-options="pageSizeOptions"
        @previous="page -= 1"
        @next="page += 1"
        @page-size-change="handlePageSizeChange"
      />
    </div>
  </div>
</template>
